import { useMemo } from 'react';
import { useCodes } from './queries';

/**
 * Calculates max depth for each node in the tree
 * @param {Object} node - Tree node
 * @returns {Object} Node with maxDepth property
 */
function calculateMaxDepth(node) {
  const children = node.children || [];
  
  let maxDepth = 0;
  
  const childrenWithDepth = children.map(child => {
    const childWithDepth = calculateMaxDepth(child);
    maxDepth = Math.max(maxDepth, childWithDepth.maxDepth + 1);
    return childWithDepth;
  });
  
  return {
    ...node,
    children: childrenWithDepth,
    maxDepth
  };
}

/**
 * Adds position context flags to nodes after tree is built and sorted
 * @param {Array} codes - Tree structure with children
 * @param {Object} parent - Parent node (for accessing position)
 * @param {Object} grandparent - Grandparent node (for accessing position)
 */
function addPositionFlags(codes, parent = null, grandparent = null) {
  // Mark last sibling at this level
  codes.forEach((code, index) => {
    code.isLastInSiblings = (index === codes.length - 1);
    code.hasChildren = code.children.length > 0;
    
    // Add parent and grandparent positions for bubble-up calculations
    code.parentPosition = parent?.position;
    code.grandparentPosition = grandparent?.position;
    code.isParentLastInSiblings = parent ? (parent.isLastInSiblings) : null;
  });
  
  // Recursively process children, passing current node as parent
  codes.forEach((code) => {
    if (code.children.length > 0) {
      addPositionFlags(code.children, code, parent);
    }
  });
}

/**
 * Builds a hierarchical tree structure from flat codes list with maxDepth
 * @param {Array} codesList - Flat array of codes
 * @returns {Array} Hierarchical tree with children and maxDepth
 */
function buildHierarchicalTree(codesList) {
  if (!codesList || !Array.isArray(codesList)) {
    return [];
  }

  // Create a map for quick lookup
  const codesMap = {};
  codesList.forEach(code => {
    codesMap[code.id] = { ...code, children: [], grandparent_id: null };
  });

  // Build the nested structure
  const rootCodes = [];
  
  codesList.forEach(code => {
    const codeWithChildren = codesMap[code.id];
    
    if (code.parent_id === null) {
      rootCodes.push(codeWithChildren);
    } else {
      const parent = codesMap[code.parent_id];
      if (parent) {
        parent.children.push(codeWithChildren);
        // Set grandparent_id based on the parent's parent_id
        codeWithChildren.grandparent_id = parent.parent_id;
      }
    }
  });

  // Sort children by position at each level
  function sortChildren(codes) {
    codes.forEach(code => {
      if (code.children.length > 0) {
        code.children.sort((a, b) => a.position - b.position);
        sortChildren(code.children);
      }
    });
  }

  rootCodes.sort((a, b) => a.position - b.position);
  sortChildren(rootCodes);

  // Calculate maxDepth for each node
  return rootCodes.map(root => calculateMaxDepth(root));
}

/**
 * Flattens hierarchical tree into a flat list with level and maxDepth properties
 * @param {Array} hierarchicalCodes - Tree structure with children
 * @param {Function} shouldSkipChildren - Optional function to determine if children should be skipped
 * @returns {Array} Flat list with level, maxDepth, and grandparent_id properties
 */
export function flattenHierarchicalCodes(hierarchicalCodes, shouldSkipChildren = () => false) {
  const flatList = [];
  
  function flattenWithDepth(codes, level = 1, parentSkipped = false) {
    codes.forEach(code => {
      // Skip this code and its children if parent was skipped
      if (parentSkipped) {
        return;
      }
      
      // Add code to flat list with level and maxDepth, removing children property
      const { children, ...codeWithoutChildren } = code;
      flatList.push({
        ...codeWithoutChildren,
        level,
        maxDepth: code.maxDepth
        // isLastInSiblings, hasChildren, isParentLastInSiblings are preserved
      });
      
      // Check if we should skip this code's children
      const skipChildren = shouldSkipChildren(code);
      
      // Recursively flatten children
      if (children && children.length > 0) {
        flattenWithDepth(children, level + 1, skipChildren);
      }
    });
  }
  
  flattenWithDepth(hierarchicalCodes);
  return flatList;
}

/**
 * Hook that fetches codes and structures them as a hierarchical tree
 * @param {string|number} projectId - Project ID
 * @returns {Object} Hierarchical codes tree and loading state with maxDepth and grandparent_id
 */
export function useCodesHierarchy(projectId) {
  // Fetch codes using React Query
  const { 
    data: codesData, 
    isLoading,
    error
  } = useCodes(projectId);

  // Process the codes into a hierarchical tree structure
  const codes = useMemo(() => {
    if (!codesData || !codesData.codes) {
      return [];
    }
    
    const hierarchicalTree = buildHierarchicalTree(codesData.codes);
    
    // Add position flags as a separate pass
    addPositionFlags(hierarchicalTree);
    
    return hierarchicalTree;
  }, [codesData]);

  return {
    codes,
    isLoading,
    error
  };
}

/**
 * Hook that fetches codes and structures them as a flat list with level, maxDepth, and grandparent_id
 * @param {string|number} projectId - Project ID
 * @returns {Object} Flat codes list with level, maxDepth, and grandparent_id properties and loading state
 */
export default function useCodesSidebar(projectId) {
  // Get hierarchical codes
  const { 
    codes: hierarchicalCodes, 
    isLoading,
    error,
  } = useCodesHierarchy(projectId);

  // Flatten the hierarchical structure
  const codes = useMemo(() => {
    return flattenHierarchicalCodes(hierarchicalCodes);
  }, [hierarchicalCodes]);

  return {
    codes,
    isLoading,
    error
  };
}